import { Link } from "react-router-dom";
import {
  ShieldCheck,
  Lock,
  FileText,
  Building2,
  Globe,
  HeartHandshake,
  Activity,
  ScrollText,
  ListChecks,
  Accessibility as A11y,
  AlertTriangle,
  Server,
  BadgeCheck,
  Cookie,
  Megaphone,
  Eye,
  MessageSquare,
  ShieldAlert,
  Lightbulb,
  ArrowRight,
} from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
} from "../../components/ui";
import { useSeo } from "../../lib/seo";

const TILES = [
  { icon: ShieldCheck, title: "Security overview", body: "Encryption, MFA, audit log, RLS pen-test.", to: "/security" },
  { icon: Lock, title: "HIPAA", body: "§164.308 / .310 / .312 controls + BAA on every covered subprocessor.", to: "/hipaa" },
  { icon: FileText, title: "Privacy policy", body: "GDPR + CCPA + general. What we collect, why.", to: "/privacy" },
  { icon: ScrollText, title: "Terms of Service", body: "Acceptable use, liability, termination.", to: "/terms" },
  { icon: Cookie, title: "Cookie policy", body: "Essential only. No third-party tracking.", to: "/cookies" },
  { icon: HeartHandshake, title: "Data Processing Agreement", body: "Standard DPA covering EU/UK transfers.", to: "/dpa" },
  { icon: BadgeCheck, title: "Business Associate Agreement", body: "HIPAA §164.504(e) compliant BAA.", to: "/baa" },
  { icon: Activity, title: "Service Level Agreement", body: "Uptime targets and credits per tier.", to: "/sla" },
  { icon: ListChecks, title: "Acceptable Use Policy", body: "What you can and cannot do on the platform.", to: "/aup" },
  { icon: Server, title: "Subprocessors", body: "Every covered subprocessor + BAA status.", to: "/subprocessors" },
  { icon: A11y, title: "Accessibility statement", body: "WCAG 2.1 AA target. Active program.", to: "/accessibility" },
  { icon: Globe, title: "GDPR rights", body: "EU data subject rights under Articles 15-22.", to: "/gdpr" },
  { icon: Eye, title: "CCPA rights", body: "California consumer privacy disclosures.", to: "/ccpa" },
  { icon: Building2, title: "Status page", body: "Uptime and incident history.", to: "/status" },
  { icon: Megaphone, title: "Responsible disclosure", body: "Security researcher program + safe harbor.", to: "/responsible-disclosure" },
  { icon: AlertTriangle, title: "Incident reporting", body: "How to report a security or privacy incident.", to: "/responsible-disclosure" },
  { icon: MessageSquare, title: "SMS terms & opt-in", body: "Twilio A2P 10DLC + TCPA disclosures. Opt-in, frequency, STOP/HELP.", to: "/sms-terms" },
  { icon: ShieldCheck, title: "Antibiotic Stewardship methodology", body: "42 CFR §483.80(a)(4) + CDC Core Elements + Loeb / McGeer references.", to: "/trust/asp-methodology" },
  { icon: ShieldAlert, title: "Drug interactions — methodology", body: "How label-grounded DDIs are sourced, pulled, and verified. Educational reference, not CDS.", to: "/trust/ddi-methodology" },
  { icon: Lightbulb, title: "In-app Help AI methodology", body: "How the in-app Help drawer + page-context AI work, what they do not do, how queries are audited without storing raw text.", to: "/trust/help-ai-methodology" },
];

export function Trust() {
  useSeo({
    title: "Trust center — WeConsult Rx",
    description:
      "WeConsult Rx trust center: security, HIPAA, privacy, BAA, DPA, SLA, subprocessors, GDPR, CCPA, accessibility, and incident reporting.",
    path: "/trust",
  });

  return (
    <>
      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Trust center</Eyebrow>
            <Heading level={1} className="mt-3">
              Compliance, shipped in code.
            </Heading>
            <Lede className="mt-6">
              We treat trust the way we treat clinical evidence: cited,
              verifiable, and re-validated on every release.
            </Lede>
          </div>
        </Container>
      </Section>

      <Section className="pt-8">
        <Container>
          <div className="grid gap-5 md:grid-cols-2 lg:grid-cols-3">
            {TILES.map((t) => {
              const Icon = t.icon;
              return (
                <Card as="article" key={t.title} className="h-full">
                  <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-forest-50 text-forest-700">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <Heading level={3} className="mt-4">
                    {t.title}
                  </Heading>
                  <p className="mt-2 text-sm text-ink-600">{t.body}</p>
                  <Link
                    to={t.to}
                    className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:text-forest-900"
                  >
                    Read
                    <ArrowRight className="h-4 w-4" aria-hidden />
                  </Link>
                </Card>
              );
            })}
          </div>
        </Container>
      </Section>
    </>
  );
}
